/**
 * /manufacturing/part/:partId — per-part drill-down (Phase 5).
 *
 * Single-part view that pulls together:
 *   - Onshape identity (part number, name, revision, material, mass)
 *   - Drift status against `manufacturing.product_variants.default_code`
 *   - Recent MO history (50 most recent, output + component roles)
 *   - Deep-link into Onshape (when document/workspace IDs resolve)
 *
 * Auth gate: same MANUFACTURING_ALLOWED_EMAILS allowlist as the rest of
 * `/manufacturing`. Unauthorised users see the access-denied alert.
 *
 * Thumbnail backfill is deferred -- Onshape's /parts payload doesn't carry
 * thumbnailInfo inline, so populating `onshape.parts.thumbnail_url`
 * requires a separate /thumbnails pass that's outside the scope of this PR.
 * When the column lands populated, the placeholder swaps to an `<img>`
 * automatically.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useManufacturingAccess } from '../hooks/useManufacturingAccess';
import { useManufacturingPartDetailQuery } from '../components/Manufacturing/Manufacturing.queries';
import { useTranslation } from '../i18n';
import { MOHistoryRow, PartDetail } from '../types/manufacturing';

const STATE_COLOR: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  draft: 'default',
  confirmed: 'info',
  progress: 'primary',
  to_close: 'warning',
  done: 'success',
  cancel: 'error',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function MOHistoryTable({ rows }: { rows: MOHistoryRow[] }) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No production orders found for this part.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 120 }}>MO</TableCell>
            <TableCell sx={{ minWidth: 90 }}>Role</TableCell>
            <TableCell sx={{ minWidth: 200 }}>Built (parent)</TableCell>
            <TableCell sx={{ minWidth: 160 }}>Progress</TableCell>
            <TableCell sx={{ minWidth: 110 }}>Planned start</TableCell>
            <TableCell sx={{ minWidth: 110 }}>Deadline</TableCell>
            <TableCell sx={{ minWidth: 110 }}>State</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const pct =
              r.qty_planned > 0 ? Math.min(100, (r.qty_produced / r.qty_planned) * 100) : 0;
            return (
              <TableRow key={`${r.mo_id}-${r.role}`} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.mo_name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.role}
                    color={r.role === 'output' ? 'success' : 'default'}
                    variant={r.role === 'output' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={r.parent_product_name ?? ''}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 240,
                      }}
                    >
                      {r.parent_product_name ?? (r.role === 'output' ? '— (this part)' : '—')}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 80 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ minWidth: 60 }}>
                      {r.qty_produced} / {r.qty_planned}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{formatDate(r.date_planned_start)}</TableCell>
                <TableCell>{formatDate(r.date_planned_finished)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.mo_state}
                    color={STATE_COLOR[r.mo_state] ?? 'default'}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DriftBadge({ part }: { part: PartDetail }) {
  if (part.drift_status === 'matched' && part.matched_variant) {
    return (
      <Tooltip title={`Variant: ${part.matched_variant.name}`}>
        <Chip
          size="small"
          color="success"
          label={`Matched: ${part.matched_variant.default_code}`}
          data-testid="manufacturing-part-drift-badge"
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="No matching product_variant.default_code in Odoo">
      <Chip
        size="small"
        color="error"
        label="Missing in Odoo"
        data-testid="manufacturing-part-drift-badge"
      />
    </Tooltip>
  );
}

// onshape.parts.id is a Postgres UUID. Validate before calling the API
// so an arbitrarily long or malformed URL segment doesn't reach the
// backend (and so the 404 alert renders a bounded string).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ThumbnailPanel({ part }: { part: PartDetail }) {
  if (part.thumbnail_url) {
    return (
      <Box
        component="img"
        src={part.thumbnail_url}
        alt={`${part.name} thumbnail`}
        loading="lazy"
        sx={{
          width: '100%',
          maxWidth: 280,
          aspectRatio: '1 / 1',
          objectFit: 'contain',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: '#fafafa',
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 280,
        aspectRatio: '1 / 1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: '#fafafa',
        color: 'text.secondary',
      }}
      data-testid="manufacturing-part-thumbnail-placeholder"
    >
      <ImageNotSupportedOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
      <Typography variant="caption">No thumbnail yet</Typography>
    </Box>
  );
}

export function ManufacturingPartPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { partId } = useParams<{ partId: string }>();
  const { hasAccess, loading: accessLoading } = useManufacturingAccess();

  // Bail out before touching the API if the URL segment isn't a well-formed
  // UUID. The backend would 404 on a junk id anyway, but this keeps the
  // request bounded and gives the user immediate feedback.
  const validId = !!partId && UUID_RE.test(partId);

  const partQuery = useManufacturingPartDetailQuery(partId, hasAccess && validId);
  const part: PartDetail | null = partQuery.data ?? null;
  const loading = partQuery.isFetching;

  // A 404 means the part isn't in the Onshape mirror — render the "not found"
  // warning rather than the generic error alert. Anything else (or an invalid
  // UUID, which we never even send) falls into the respective branch.
  const errorStatus =
    partQuery.error && typeof partQuery.error === 'object' && 'response' in partQuery.error
      ? (partQuery.error as { response?: { status?: number } }).response?.status
      : undefined;
  const notFound = (hasAccess && !validId) || errorStatus === 404;
  const showError = partQuery.isError && errorStatus !== 404;

  if (accessLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }} data-testid="manufacturing-access-denied">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Alert severity="warning">
          You do not have access to the /manufacturing page. If you believe this is wrong, ask an
          admin to add your email to <code>MANUFACTURING_ALLOWED_EMAILS</code>.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }} data-testid="manufacturing-part-page">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/manufacturing')}>
          Back to Manufacturing
        </Button>
      </Box>

      {notFound && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Part <code>{partId}</code> not found in the Onshape mirror. It may have been deleted, or
          the snapshot has not run since the part was created.
        </Alert>
      )}

      {showError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('manufacturing.failedToLoadPartDetail')}
        </Alert>
      )}

      {loading && (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
        </Stack>
      )}

      {part && !loading && (
        <>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              alignItems={{ md: 'flex-start' }}
            >
              <ThumbnailPanel part={part} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                  >
                    {part.part_number ?? '(no part number)'}
                  </Typography>
                  <DriftBadge part={part} />
                  {part.revision && (
                    <Chip size="small" label={`rev ${part.revision}`} variant="outlined" />
                  )}
                  {part.state && <Chip size="small" label={part.state} variant="outlined" />}
                </Stack>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                  {part.name}
                </Typography>
                <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Material
                    </Typography>
                    <Typography variant="body2">{part.material ?? '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Mass
                    </Typography>
                    <Typography variant="body2">
                      {part.mass_kg !== null ? `${part.mass_kg.toFixed(3)} kg` : '—'}
                    </Typography>
                  </Box>
                  {part.matched_variant && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Odoo variant
                      </Typography>
                      <Typography variant="body2">{part.matched_variant.name}</Typography>
                    </Box>
                  )}
                </Stack>
                {part.onshape_url && (
                  <Box sx={{ mt: 2 }}>
                    <Link
                      href={part.onshape_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      Open in Onshape <LaunchIcon fontSize="inherit" />
                    </Link>
                  </Box>
                )}
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }} data-testid="manufacturing-part-mo-history">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Production-order history</Typography>
              <Typography variant="caption" color="text.secondary">
                {part.mos_truncated
                  ? 'Showing 50 most recent (older orders clipped)'
                  : `${part.mos.length} shown`}
              </Typography>
            </Stack>
            {part.mos_truncated && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Showing the 50 most recent MOs. Older orders are clipped.
              </Alert>
            )}
            <MOHistoryTable rows={part.mos} />
          </Paper>
        </>
      )}
    </Container>
  );
}
